import { ButtonLink } from '@app/components/base/ButtonLink';
import { Page } from '@app/components/page/Page';
import { StationIndicators } from '@app/components/stations/StationIndicators';
import { StationCtxProvider } from '@app/core/containers/StationCtx';
import { canEditStation } from '@app/core/stations/helpers';
import { useStation } from '@app/core/stations/hooks/useStation';
import { ConnAdminStationsGroupsPermissionsView } from '@app/modules/admin/screens/stations/groups-permissions/AdminStationsGroupsPermissionsView';
import { ConnAdminStationsUsersPermissionsView } from '@app/modules/admin/screens/stations/users-permissions/AdminStationsUsersPermissionsView';
import { StationInfo } from '@app/modules/admin/screens/stations/view/StationInfo';
import { capitalizeFirst } from '@mi-front/core';
import EditIcon from '@mui/icons-material/Edit';
import { Box } from '@mui/material';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { AdminStationsBreadcrumbs } from '../header';
import { StationLabel } from '../list/StationLabel';
import { TabKey, useAdminStationsViewTabs } from './useAdminStationsViewTabs';

/**
 * Admin station detail page with header tabs for basic info and permissions.
 */
export function AdminStationsViewPage() {
  const { slug } = useParams<{ slug: string }>();
  const { $t, formatMessage } = useIntl();
  const station = useStation(slug);
  const canEdit = canEditStation(station);
  const { tabs, activeTab } = useAdminStationsViewTabs(station);

  return (
    <StationCtxProvider station={station}>
      <Page
        htmlTitle={`${capitalizeFirst(
          $t({
            id: 'common.entities.station',
            defaultMessage: 'emisora',
          }),
        )} ${station.name}`}
      >
        <Page.Header
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <StationLabel station={station} indicatorsDisplay={false} />
              <StationIndicators station={station} variant="chip" />
            </Box>
          }
          breadcrumbs={<AdminStationsBreadcrumbs title={station.name} />}
          tabs={tabs}
          actions={
            canEdit ? (
              <ButtonLink
                to={`/admin/stations/${station.slug}/edit`}
                variant="outlined"
                startIcon={<EditIcon />}
              >
                {formatMessage({
                  id: 'common.actions.edit',
                  defaultMessage: 'Editar',
                })}
              </ButtonLink>
            ) : null
          }
        />
        <Page.Content>
          {activeTab === TabKey.INFO && <StationInfo station={station} />}
          {activeTab === TabKey.GROUPS_PERMISSIONS && (
            <ConnAdminStationsGroupsPermissionsView slug={station.slug} />
          )}
          {activeTab === TabKey.USERS_PERMISSIONS && (
            <ConnAdminStationsUsersPermissionsView slug={station.slug} />
          )}
        </Page.Content>
      </Page>
    </StationCtxProvider>
  );
}
